import { useEffect, useRef } from 'react';
import { useResourceAccountStore, useResourceAccountActions } from '@/store/resourceAccountStore';
import { useUserToken } from '@/store/userStore';

/**
 * 自动管理资源账户数据的Hook
 * 
 * 此Hook会：
 * 1. 在组件挂载时自动检查并获取资源账户数据（如果需要）
 * 2. 在用户登录状态改变时重新获取数据
 * 3. 在数据过期时自动刷新
 * 
 * @param options 配置选项
 * @returns 资源账户状态和方法
 * 
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { account, isLoading, refresh } = useResourceAccount();
 *   
 *   if (isLoading) return <div>加载中...</div>;
 *   if (!account) return <div>未找到资源账户</div>;
 *   
 *   return (
 *     <div>
 *       <p>月度配额剩余: {account.monthlyQuota.remaining}</p>
 *       <p>积分余额: {account.points.available}</p>
 *       <button onClick={refresh}>刷新</button>
 *     </div>
 *   );
 * }
 * ```
 */
export function useResourceAccount(options?: {
  /** 是否自动加载数据（默认：true） */
  autoLoad?: boolean;
  /** 是否在数据过期时自动刷新（默认：true） */
  autoRefresh?: boolean;
  /** 刷新间隔（毫秒，默认：5分钟） */
  refreshInterval?: number;
}) {
  const {
    autoLoad = true,
    autoRefresh = true,
    refreshInterval = 5 * 60 * 1000, // 5分钟
  } = options || {};

  const account = useResourceAccountStore((state) => state.account);
  const isLoading = useResourceAccountStore((state) => state.isLoading);
  const error = useResourceAccountStore((state) => state.error);
  const lastUpdated = useResourceAccountStore((state) => state.lastUpdated);
  
  const { refreshAccount, shouldRefresh } = useResourceAccountActions();
  const userToken = useUserToken();
  
  const intervalRef = useRef<NodeJS.Timeout | undefined>(undefined);

  // 初始加载和登录状态变化时加载数据
  useEffect(() => {
    if (!userToken?.accessToken) {
      // 用户未登录，清理数据
      return;
    }

    if (autoLoad) {
      // 检查是否需要刷新
      if (shouldRefresh()) {
        refreshAccount();
      }
    }
  }, [userToken?.accessToken, autoLoad, refreshAccount, shouldRefresh]);

  // 自动刷新逻辑
  useEffect(() => {
    if (!autoRefresh || !userToken?.accessToken) {
      return;
    }

    // 设置定时刷新
    intervalRef.current = setInterval(() => {
      if (shouldRefresh()) {
        refreshAccount();
      }
    }, refreshInterval);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [autoRefresh, refreshInterval, userToken?.accessToken, refreshAccount, shouldRefresh]);

  return {
    /** 资源账户数据 */
    account,
    /** 是否正在加载 */
    isLoading,
    /** 错误信息 */
    error,
    /** 最后更新时间 */
    lastUpdated,
    /** 手动刷新数据 */
    refresh: refreshAccount,
    /** 是否需要刷新 */
    needsRefresh: shouldRefresh(),
  };
}

/**
 * 仅获取月度配额的Hook（优化性能）
 * 
 * @example
 * ```tsx
 * function QuotaDisplay() {
 *   const monthlyQuota = useMonthlyQuota();
 *   return <div>剩余: {monthlyQuota?.remaining || 0}</div>;
 * }
 * ```
 */
export function useMonthlyQuota() {
  return useResourceAccountStore((state) => state.account?.monthlyQuota);
}

/**
 * 仅获取积分余额的Hook（优化性能）
 * 
 * @example
 * ```tsx
 * function PointsDisplay() {
 *   const points = usePointsBalance();
 *   return <div>可用积分: {points?.available || 0}</div>;
 * }
 * ```
 */
export function usePointsBalance() {
  return useResourceAccountStore((state) => state.account?.points);
}

/**
 * 仅获取每日免费配额的Hook（优化性能）
 * 
 * @example
 * ```tsx
 * function DailyFreeDisplay() {
 *   const dailyFree = useDailyFreeQuota();
 *   return <div>今日剩余: {dailyFree?.remaining || 0}</div>;
 * }
 * ```
 */
export function useDailyFreeQuota() {
  return useResourceAccountStore((state) => state.account?.dailyFree);
}

/**
 * 仅获取用户层级的Hook（优化性能）
 * 
 * @example
 * ```tsx
 * function UserTierBadge() {
 *   const tier = useUserTier();
 *   return <Badge>{tier}</Badge>;
 * }
 * ```
 */
export function useUserTier() {
  return useResourceAccountStore((state) => state.account?.userTier);
}

export default useResourceAccount;

